import { useState, cloneElement, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { Button } from 'foundations-components/transitional/components/Button';
import { CharacterCounter } from 'foundations-components/transitional/components/CharacterCounter';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { Text } from 'foundations-components/components/Text';
import { TextArea } from 'foundations-components/transitional/components/TextArea';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { FeedbackSurveyResponses } from './internal/FeedbackSurveyResponses';
import { useTrackSurveyView, useHideSurvey } from '../utils/surveyUtils';
import { submitCsatSurvey } from '../api/surveyApi';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CustomDialogBody = styled.div.withConfig({
  displayName: "FeedbackSurveyModalContent__CustomDialogBody",
  componentId: "sc-1sc56jn-0"
})(({
  theme: _theme
}) => {
  const theme = _theme;
  return {
    padding: theme.space['1000'],
    borderRadius: theme.borderRadius['100'],
    backgroundColor: theme.color['fill-surface-default'],
    overflowY: 'auto',
    flexGrow: 1,
    flexShrink: 1
  };
});
const FeedbackSurveyModalContent = ({
  buttonText,
  footer,
  placeholder,
  rating = 2,
  subTitle,
  title,
  surveyConfig,
  surveyId,
  selectableResponses = [],
  onClose,
  onOpenComplete,
  onSubmitComplete
}) => {
  const [followUp, setFollowUp] = useState();
  const selectedResponses = useRef(new Set());
  useHideSurvey(surveyConfig);
  useTrackSurveyView(surveyConfig);
  const handleSurveySubmit = useCallback(() => {
    if (followUp || selectedResponses.current.size > 0) {
      const {
        contactId
      } = surveyConfig.webRespondent || {};
      const selectedResponsesString = Array.from(selectedResponses.current).join(', ');

      // Concatenate follow up and selected responses following these rules:
      // If there is a follow up, and there are selected responses, add a separator: " - "
      // If there is a follow up, and there are no selected responses, add the follow up without a separator
      // If there is no follow up, and there are selected responses, add the selected responses without a separator
      const followUpString = `${followUp || ''}${followUp && selectedResponsesString.length > 0 ? ' - ' : ''}${selectedResponsesString || ''}`;
      /* hs-eslint ignored failing-rules */
      /* error has been caught in submitCsatSurvey */
      /* eslint-disable-next-line promise/catch-or-return */
      submitCsatSurvey({
        surveyId,
        contactId,
        rating,
        followUp: followUpString
      }).finally(() => onSubmitComplete === null || onSubmitComplete === void 0 ? void 0 : onSubmitComplete(followUpString));
    }
    onClose();
  }, [surveyConfig, rating, followUp, onSubmitComplete, onClose, surveyId]);
  const handleSelectResponse = useCallback((response, isSelected) => {
    if (isSelected) {
      selectedResponses.current.add(response);
    } else {
      selectedResponses.current.delete(response);
    }
  }, []);
  return /*#__PURE__*/_jsx(Modal, {
    "data-test-id": "feedback-survey-modal",
    onOpenComplete: onOpenComplete,
    use: "conversational",
    width: 550,
    children: /*#__PURE__*/_jsxs(CustomDialogBody, {
      children: [/*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose
      }), /*#__PURE__*/_jsx(Text, {
        as: "h2",
        typography: "heading-300",
        display: "block",
        paddingBlockEnd: "500",
        children: title
      }), selectableResponses.length > 0 && /*#__PURE__*/_jsx(FeedbackSurveyResponses, {
        onSelectResponse: handleSelectResponse,
        responses: selectableResponses
      }), subTitle && /*#__PURE__*/_jsx(Text, {
        as: "p",
        display: "block",
        textAlign: "start",
        children: subTitle
      }), /*#__PURE__*/_jsx(CharacterCounter, {
        counter: "down",
        max: 1500,
        maxLength: 1500,
        renderInput: ({
          onChange,
          value
        }) => /*#__PURE__*/_jsx(TextArea, {
          rows: 3,
          placeholder: placeholder,
          autoFocus: true,
          value: value,
          onChange: onChange
        }),
        onChange: ({
          target: {
            value
          }
        }) => {
          setFollowUp(value);
        }
      }), footer ? ( /*#__PURE__*/cloneElement(footer, {
        followUp,
        onSubmitComplete,
        surveyConfig
      })) : /*#__PURE__*/_jsx(Button, {
        onClick: handleSurveySubmit,
        size: "sm",
        use: "primary",
        children: buttonText
      })]
    })
  });
};
export default FeedbackSurveyModalContent;